<template>
  <Select
    ref="select"
    class="policyholder-selector-wrapper"
    :placeholder="placeholder"
    :value="selectedPolicyholderId"
    :options="internalList"
    :empty-text="emptyStateText"
    type="autocomplete"
    value-key="id"
    :label-key="displayLabelKey"
    :disabled="isDisabled"
    :show-icon="showIcon"
    :autocomplete-mask="mask"
    :is-loading="isLoading || isSearching"
    filter-mode="remote"
    :hide-dropdown-when-empty="true"
    :error-messages="validationErrors"
    @beforeChange="onBeforeChange"
    @change="handlePolicyholderSelect"
    @search="onSearchInput"
  >
    <template #option="{ option }">
      <template v-if="option">
        <p class="policyholder-selector__info-text">
          <strong v-if="policyholderType !== 'legal'">ПІБ</strong>
          <strong v-else-if="policyholderType === 'legal'">Компанія</strong>
          {{ option.fio }}
        </p>
        <p class="policyholder-selector__info-text">
          <strong v-if="policyholderType !== 'legal'">ІНН</strong>
          <strong v-else-if="policyholderType === 'legal'">ЄДРПОУ</strong>
          {{ option.identification_number }}
        </p>
        <p
          v-if="policyholderType !== 'legal'"
          class="policyholder-selector__info-text"
        >
          <strong>Дата народження</strong> {{ option.date_of_birth }}
        </p>
      </template>
    </template>
  </Select>
</template>

<script>
import { mapActions } from 'vuex';
import { policyholderTypes } from '@/entities/insurance/config/policyholder-type';
import { Select } from '@/shared/ui/form';

const CLIENT_SEARCH_DELAY = 1000;

export default {
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number, Object, null],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    emptyStateText: {
      type: String,
      default: 'Нічого немає',
    },
    policyholder: {
      type: [String, Number, null],
      default: null,
    },
    policyholderType: {
      type: String,
      default: 'natural',
      validation: (value) => ['natural', 'legal', 'beneficiary'].includes(value),
    },
    policyholdersList: {
      type: Array,
      default: () => [],
    },
    mask: {
      type: [String, Function, Boolean],
      default: null
    },
    autoOpenOnUpdateList: {
      type: Boolean,
      default: false,
    },
    autoSelectSingle: {
      type: Boolean,
      default: true,
    },
    validationErrors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isSearching: false,
      isUserSearching: false,
      isSelecting: false,
      searchInput: '',
      selectedPolicyholderId: null,
      searchTimeoutId: null,
      internalList: [],
      previousPolicyholderIds: [],
      lastSearchedPolicyholderType: null,
      pendingValueEmit: null,
    };
  },
  mounted() {
    this.internalList = [...this.policyholdersList];
    this.previousPolicyholderIds = this.getPolicyholderIds(this.policyholdersList);
    this.trySelectPolicyholderFromProp();

    if (this.pendingSelectEmitId) {
      this.emitSelectForObject(this.pendingSelectEmitId);
    }
  },
  beforeDestroy() {
    this.clearSearchTimeout();
  },
  components: { Select },
  computed: {
    policyholderTypes() {
      return policyholderTypes;
    },
    displayLabelKey() {
      if (this.selectedDisplayKey === 'taxNumber') {
        return 'identification_number';
      }
      return 'fio';
    },
  },
  methods: {
    ...mapActions('policyholderModule', ['searchPolicyholders']),
    getPolicyholderTypeId(key) {
      return this.policyholderTypes?.find(t => t.key === key)?.id ?? null;
    },
    getPolicyholderIds(list) {
      if (!list || !Array.isArray(list)) return [];
      return list.map(c => c?.id).filter(Boolean).sort((a, b) => a - b);
    },
    areClientListsEqual(ids1, ids2) {
      if (ids1.length !== ids2.length) return false;
      return ids1.every((id, index) => id === ids2[index]);
    },
    handlePolicyholderSelect(selectedId) {
      this.isUserSearching = false;
      const selectedOption = this.internalList.find(option => option.id === selectedId);
      this.selectedPolicyholderId = selectedOption ? selectedOption.id : null;
      this.$emit('input', selectedOption || null);
      this.$emit('change', selectedOption || null);
    },
    onBeforeChange(newValue, oldValue) {
      this.$emit('beforeChange', newValue, oldValue);
    },
    onSearchInput(input) {
      this.searchInput = input;
      this.isUserSearching = true;
      this.resetSelection();
      this.scheduleSearch(input);
    },
    resetSelection() {
      this.selectedPolicyholderId = null;
    },
    clearSearchTimeout() {
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
        this.searchTimeoutId = null;
      }
    },
    async performSearch(input) {
      if (!input.length) {
        this.internalList = [];
        this.isUserSearching = false;
        return;
      }

      this.isSearching = true;

      try {
        const results = await this.searchPolicyholders({
          searchText: input,
          customerType: this.getPolicyholderTypeId(this.policyholderType)
        });
        this.internalList = results || [];

        if (this.pendingSelectEmitId) {
          this.emitSelectForObject(this.pendingSelectEmitId);
        }

        this.$emit('changePolicyholdersList');
        this.applyListUpdateLogic();
      } finally {
        this.isSearching = false;
      }
    },
    scheduleSearch(input) {
      this.clearSearchTimeout();

      if (this.isSelecting) return;

      if (!input.length) {
        this.isUserSearching = false;
        if (this.selectedPolicyholderId === null) {
          this.internalList = [];
        }
        return;
      }

      this.searchTimeoutId = setTimeout(() => {
        this.searchTimeoutId = null;
        this.performSearch(input);
      }, CLIENT_SEARCH_DELAY);
    },
    applyListUpdateLogic() {
      if (this.policyholder !== null && this.policyholder !== undefined) {
        const propOption = this.internalList.find(c => c?.id === this.policyholder);
        if (propOption) {
          this.isSelecting = true;
          this.$emit('beforeChange', propOption, this.value);
          this.selectedPolicyholderId = propOption.id;
          this.$emit('input', propOption);
          this.$emit('change', propOption);
          this.$emit('select', propOption);
          this.$nextTick(() => { this.isSelecting = false; });
          return;
        }
      }

      if (this.value && this.value.id) {
        const existingOption = this.internalList.find(c => c?.id === this.value.id);
        if (existingOption) {
          this.selectedPolicyholderId = existingOption.id;
          this.$emit('select', existingOption);
          this.pendingSelectEmitId = null;
          return;
        } else if (!this.isUserSearching) {
          this.resetSelection();
          this.$emit('input', null);
          this.$emit('change', null);
          return;
        }
      } else if (!this.isUserSearching) {
        this.resetSelection();
      }

      if (this.autoSelectSingle && this.internalList.length === 1 && !this.isUserSearching) {
        this.handlePolicyholderSelect(this.internalList[0].id);
      }
    },
    policyholderPropExistsInList() {
      if (this.policyholder === null || this.policyholder === undefined) return false;
      return this.internalList?.some(option => option?.id === this.policyholder);
    },
    trySelectPolicyholderFromProp() {
      if (this.policyholder === null || this.policyholder === undefined) return;

      const existingOption = this.internalList?.find(
        option => option?.id === this.policyholder
      );

      if (existingOption) {
        this.isSelecting = true;
        this.$emit('beforeChange', existingOption, this.value);
        this.selectedPolicyholderId = existingOption.id;
        this.$emit('input', existingOption);
        this.$emit('change', existingOption);
        this.$nextTick(() => { this.isSelecting = false; });
      }
    },
    emitSelectForObject(id) {
      const fullObject = this.internalList.find(c => c?.id === id);
      if (fullObject) {
        this.pendingSelectEmitId = null;
        this.$emit('select', fullObject);
        return true;
      }
      this.pendingSelectEmitId = id;
      return false;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          const newId = typeof newValue === 'object' ? newValue.id : newValue;
          if (this.selectedPolicyholderId !== newId) {
            this.selectedPolicyholderId = newId;
            this.emitSelectForObject(newId);
          }
        } else {
          this.selectedPolicyholderId = null;
          this.pendingSelectEmitId = null;
        }
      },
    },
    policyholdersList: {
      handler(newList) {
        const newIds = this.getPolicyholderIds(newList);

        if (this.areClientListsEqual(newIds, this.previousPolicyholderIds)) return;

        this.previousPolicyholderIds = newIds;
        this.searchInput = '';

        const merged = [...newList];
        if (this.value && this.value.id && !merged.some(c => c?.id === this.value.id)) {
          merged.unshift(this.value);
        }
        this.internalList = merged;

        if (this.pendingSelectEmitId) {
          this.emitSelectForObject(this.pendingSelectEmitId);
        }

        this.$emit('changePolicyholdersList');
        this.applyListUpdateLogic();

        if (this.autoOpenOnUpdateList && this.internalList.length > 0) {
          this.$nextTick(() => {
            this.$refs.select?.openDropdown();
          });
        }
      },
      deep: true,
    },
    policyholder(newId) {
      if (newId === null || newId === undefined) {
        this.resetSelection();
        this.$emit('input', null);
        this.$emit('change', null);
        return;
      }

      const existingOption = this.internalList?.find(option => option?.id === newId);

      if (existingOption) {
        this.isSelecting = true;
        this.$emit('beforeChange', existingOption, this.value);
        this.selectedPolicyholderId = existingOption.id;
        this.$emit('input', existingOption);
        this.$emit('change', existingOption);
        this.$nextTick(() => { this.isSelecting = false; });
      } else if (this.internalList?.length > 0) {
        this.resetSelection();
        this.$emit('input', null);
        this.$emit('change', null);
      }
    },
    policyholderType(newType) {
      if (this.isSelecting) return;
      if (newType === this.lastSearchedPolicyholderType) return;

      this.lastSearchedPolicyholderType = newType;

      if (this.value) return;

      const input = this.searchInput;
      this.clearSearchTimeout();
      if (input) this.performSearch(input);
    },
  },
};
</script>

<style lang="scss" scoped>
.policyholder-selector__info-text {
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  strong {
    font-weight: 600;
  }
}
</style>
